import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

export const PROFILE_QUERY_KEYS = {
  byId: (userId: string) => ['profiles', userId] as const,
  byUsername: (username: string) => ['profiles', 'username', username] as const,
};

export const useProfile = (userId: string | null) => {
  return useQuery({
    queryKey: PROFILE_QUERY_KEYS.byId(userId || ''),
    queryFn: async () => {
      if (!userId) throw new Error('User ID is required');

      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, email, username, has_seen_onboarding')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });
};

export const useProfileByUsername = (username: string | null) => {
  return useQuery({
    queryKey: PROFILE_QUERY_KEYS.byUsername(username || ''),
    queryFn: async () => {
      if (!username) throw new Error('Username is required');

      const { data, error } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('username', username)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!username,
  });
};

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      updates,
    }: {
      userId: string;
      updates: Partial<{ has_seen_onboarding: boolean }>;
    }) => {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: PROFILE_QUERY_KEYS.byId(data.id) });
    },
  });
};
